/**
 * OFAC result with full transparency (Bill 2026-10-07, for Risk Management):
 *   - WHICH list was searched and how (quoted from JHA's OFAC Search reference),
 *   - WHAT came back: every possible match grouped under its listed entry, program codes as
 *     separate chips, which word of the screened name lines up with which listed word,
 *   - WHY it reads Clear / Historical / Hit: the rollup rule with the step that decided it,
 *   - and the exact pill + banner the officer app shows for the same status.
 * Everything comes from the server's transparency block; nothing is computed here.
 */

import type { CandidateGroup, OfacTestResponse, TokenMatch } from '@/services/verificationTestApi';

import { Banner, Panel, Pill, RawResponse, RuleSteps, SectionTitle, SettingsTable, fmtDate } from './consoleUi';

const STANDING: Record<string, { tone: 'err' | 'warn' | 'muted'; label: string }> = {
  current: { tone: 'err', label: 'Current SDN listing' },
  unknown: { tone: 'err', label: 'Cannot be checked — treated as current' },
  delisted: { tone: 'warn', label: 'No longer listed' },
};

function TokenChips({ tokens }: { tokens: TokenMatch[] }): JSX.Element {
  if (tokens.length === 0) return <span className="vc-muted">—</span>;
  return (
    <span className="vc-chips">
      {tokens.map((t, i) => (
        <span
          key={i}
          className={`vc-chip${t.kind === 'same' ? ' vc-chip--ok' : t.kind === 'sounds-alike' ? ' vc-chip--warn' : ''}`}
          title={t.kind === 'sounds-alike' ? `Sounds alike: both are Soundex ${t.soundex}` : t.kind === 'same' ? 'Same word' : 'No listed word lines up with this one'}
        >
          {t.inputToken}
          {t.kind === 'same' && ' = '}
          {t.kind === 'sounds-alike' && ` ≈ ${t.listedToken ?? ''}`}
          {t.kind === 'none' && ' ✕'}
        </span>
      ))}
    </span>
  );
}

function EntityCard({ g }: { g: CandidateGroup }): JSX.Element {
  const st = STANDING[g.standing] ?? STANDING.current;
  return (
    <div className={`vc-entity${g.standing !== 'delisted' ? ' vc-entity--current' : ''}`}>
      <div className="vc-entity__head">
        <span className="vc-entity__name">{g.listedName}</span>
        <Pill tone={st.tone}>{st.label}</Pill>
        {g.listedType && <Pill tone="muted">{g.listedType}</Pill>}
        {g.treasuryEntryId && <span className="vc-muted vc-mono">Treasury entry {g.treasuryEntryId}</span>}
      </div>
      <div className="vc-entity__body">
        <div className="vc-row" style={{ alignItems: 'center' }}>
          <span className="vc-field__label">Sanctions programs</span>
          {g.programs.length === 0
            ? <span className="vc-muted">None returned</span>
            : <span className="vc-chips">{g.programs.map((p) => <span key={p} className="vc-chip">{p}</span>)}</span>}
        </div>
        {g.details.length > 0 && <p className="vc-hint">Details to compare with the customer: {g.details.join(' · ')}</p>}
        {g.remarks.length > 0 && <p className="vc-hint">Listing remarks: {g.remarks.join(' · ')}</p>}
        {g.nameCheckNote && <Banner tone="warn">{g.nameCheckNote}</Banner>}
        <div className="vc-table-wrap">
          <table className="vc-table">
            <thead>
              <tr><th>Matched as</th><th>Kind of match</th><th>Screened name vs listed name</th><th>Why it counts this way</th></tr>
            </thead>
            <tbody>
              {g.rows.map((r) => (
                <tr key={r.aliasId + r.matchRef}>
                  <td>
                    {r.aliasName || <span className="vc-muted">No alias text returned — the listed name above is what matched</span>}
                    {r.aliasKind && <div className="vc-muted">{r.aliasKind}</div>}
                    <div className="vc-muted vc-mono">ID {r.aliasId || '—'}{r.matchRef ? ` · ref ${r.matchRef}` : ''}</div>
                  </td>
                  <td>{r.matchTypeLabel}<div className="vc-muted vc-mono">{r.matchType}</div></td>
                  <td><TokenChips tokens={r.tokens} /></td>
                  <td>
                    <Pill tone={(STANDING[r.standing] ?? STANDING.current).tone}>{(STANDING[r.standing] ?? STANDING.current).label}</Pill>
                    <div className="vc-hint" style={{ marginTop: 4 }}>{r.standingReason}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function OfacResultPanel({ r }: { r: OfacTestResponse }): JSX.Element {
  const t = r.transparency;
  if (!t) {
    return (
      <Panel title="OFAC screening" tone="muted">
        <p className="vc-hint">Result: {r.status}. This server did not return the explanation — it may be an older version.</p>
      </Panel>
    );
  }
  const v = t.verdict;
  const svc = t.searchService;
  return (
    <Panel
      title="OFAC screening"
      tone={v.tone}
      actions={<><Pill tone="info">Live search</Pill><span className="vc-muted">{r.elapsedMs} ms</span></>}
    >
      <div className="vc-verdict">
        <Pill tone={v.tone}>{v.officerPill}</Pill>
        <span className="vc-verdict__text">{v.plain}</span>
      </div>
      <Banner tone={v.commitAllowed ? 'ok' : 'err'} title={v.commitAllowed ? 'Account opening can continue' : 'Account opening stops here'}>
        {v.commitText}
      </Banner>
      {v.officerBanner && (
        <div>
          <SectionTitle>What the officer sees for this result</SectionTitle>
          <Banner tone={v.tone}>{v.officerBanner}</Banner>
        </div>
      )}

      <div className="vc-two">
        <div>
          <SectionTitle>What was searched</SectionTitle>
          <dl className="vc-kv">
            <dt>Service</dt><dd>{svc.name}</dd>
            <dt>List searched</dt><dd>{svc.listsSearched}<div className="vc-muted">{svc.listsSource}</div></dd>
            <dt>Kind of matching</dt><dd>{svc.matching}<div className="vc-muted">{svc.matchingSource}</div></dd>
            <dt>Match score</dt><dd>{svc.score}</dd>
            <dt>Sent</dt><dd>“{t.request.comName}” as {t.request.comNameType === 'Org' ? 'a business (Org)' : 'a person (Indv)'}, up to {t.request.count} results. {svc.input}</dd>
            <dt>Returned</dt>
            <dd>
              {t.returned.sentRec} possible match{t.returned.sentRec === 1 ? '' : 'es'}
              {t.returned.totRec != null ? ` of ${t.returned.totRec} selected` : ''}
              {t.returned.capped && <div style={{ color: 'var(--pcb-red)' }}>The search hit its 10-result limit — more possible matches may exist than are shown.</div>}
            </dd>
          </dl>
        </div>
        <div>
          <SectionTitle>Bank&rsquo;s SDN copy used to sort the matches</SectionTitle>
          <dl className="vc-kv">
            <dt>Entries</dt><dd>{t.sdnCopy.totalEntities.toLocaleString()} ({t.sdnCopy.currentListEntities.toLocaleString()} from Treasury&rsquo;s current list file) · {t.sdnCopy.aliasRows.toLocaleString()} aliases</dd>
            <dt>Lists in the copy</dt>
            <dd>
              {t.sdnCopy.lists.length === 0 ? 'None recorded'
                : <span className="vc-chips">{t.sdnCopy.lists.map((l) => <span key={l.name} className="vc-chip">{l.name} · {l.count.toLocaleString()}</span>)}</span>}
            </dd>
            <dt>Program codes</dt><dd>{t.sdnCopy.programCount} different codes (listed in “What is checked” above)</dd>
            <dt>Treasury data as of</dt><dd>{fmtDate(t.sdnCopy.treasuryDataAsOf, true)}</dd>
            <dt>Last updated</dt><dd>{fmtDate(t.sdnCopy.lastSyncedAt)}</dd>
          </dl>
          <p className="vc-hint" style={{ marginTop: 6 }}>The copy decides only current vs no longer listed. It is never searched by name for the result.</p>
        </div>
      </div>

      <div>
        <SectionTitle>Possible matches ({t.groups.length} listed {t.groups.length === 1 ? 'entry' : 'entries'})</SectionTitle>
        {t.groups.length === 0
          ? <p className="vc-hint">None — Core Director returned no possible matches for this name.</p>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-6)' }}>
              <p className="vc-hint">Word chips: green = the same word, amber = sounds alike (same Soundex code), ✕ = no listed word lines up. JHA does not say which words it matched beyond the match reference; this is a reading aid.</p>
              {t.groups.map((g) => <EntityCard key={g.key} g={g} />)}
            </div>
          )}
      </div>

      <div>
        <SectionTitle>How the result was decided</SectionTitle>
        <RuleSteps steps={t.rules} />
      </div>

      <div>
        <SectionTitle>Settings that touch this result</SectionTitle>
        <SettingsTable rows={t.settings} />
      </div>

      <RawResponse text={r.rawResponse} label="Show raw response from Core Director" />
    </Panel>
  );
}
